import React, { useEffect, useState, useRef } from 'react';
import { BlocManagementVouchers, VoucherViewState } from '../../../bloc/BlocManagementVouchers';
import { VoucherWithCodes, CreateVoucherRequest, UpdateVoucherRequest } from '../../../../types/voucher.types';
import StreamBuilder from '../../../components/common/StreamBuilder';
import Button from '../../../components/common/Button';
import AdminProtectedRoute from '../../../components/admin/AdminProtectedRoute';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdmin } from '../../../../base/AdminContext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './VoucherRewardPage.css';
import VoucherRewardForm from './components/VoucherRewardForm';


const VoucherRewardPage: React.FC = () => {
    const { checkPermission } = useAdmin();
    const [bloc] = useState(() => new BlocManagementVouchers());
    const [searchTerm, setSearchTerm] = useState('');
    const [showPageHelp, setShowPageHelp] = useState<boolean>(false);
    const [selectedStatus, setSelectedStatus] = useState<string>('');
    const [selectedVehicleType, setSelectedVehicleType] = useState<string>('');
    const toast = useRef<Toast>(null);

    // Check permissions for voucher management
    const canAddVoucher = checkPermission('voucher_management', 'vouchers_reward', 'add');
    const canEditVoucher = checkPermission('voucher_management', 'vouchers_reward', 'edit');
    const canDeleteVoucher = checkPermission('voucher_management', 'vouchers_reward', 'delete');

    useEffect(() => {
        // Set filter for reward vouchers only with point cost > 0
        bloc.setFilters({
            voucher_type: 'reward' as any,
            is_point_voucher: true
        });
        bloc.initialize();
        return () => bloc.dispose?.();
    }, [bloc]);

    // Search handler with debounce
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            bloc.setFilters({
                search: searchTerm,
                voucher_type: 'reward' as any,
                is_point_voucher: true
            });
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, bloc]);

    // Filter handlers
    const handleStatusFilter = async (status: string) => {
        setSelectedStatus(status);
        bloc.setFilters({
            ...bloc.info.filters,
            status: status ? status as 'active' | 'expired' | 'upcoming' : undefined,
            voucher_type: 'reward' as any,
            is_point_voucher: true
        });
    };

    const handleVehicleTypeFilter = async (vehicleType: string) => {
        setSelectedVehicleType(vehicleType);
        bloc.setFilters({
            ...bloc.info.filters,
            voucher_type_vehicle: vehicleType ? vehicleType as 'car' | 'motorbike' | 'both' : undefined,
            voucher_type: 'reward' as any,
            is_point_voucher: true
        });
    };

    // Clear filters
    const clearFilters = async () => {
        setSearchTerm('');
        setSelectedStatus('');
        setSelectedVehicleType('');
        bloc.setFilters({
            voucher_type: 'reward' as any,
            is_point_voucher: true
        });
    };

    // Refresh handler
    const handleRefresh = async () => {
        await bloc.refreshVouchers();
    };

    const handleDeleteVoucher = (voucher: VoucherWithCodes) => {
        if (!canDeleteVoucher) {
            console.warn('Không có quyền xóa voucher');
            return;
        }

        confirmDialog({
            message: `Bạn có chắc chắn muốn xóa voucher đổi điểm "${voucher.code}" này? Hành động này không thể hoàn tác.`,
            header: 'Xác nhận xóa',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Xóa',
            rejectLabel: 'Hủy',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await bloc.deleteVoucher(voucher.id);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: 'Xóa voucher đổi điểm thành công',
                        life: 3000
                    });
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: 'Không thể xóa voucher đổi điểm',
                        life: 5000
                    });
                }
            }
        });
    };

    const handleCloneVoucher = (voucher: VoucherWithCodes) => {
        if (!canAddVoucher) {
            console.warn('Không có quyền sao chép voucher');
            return;
        }

        confirmDialog({
            message: `Bạn có muốn sao chép voucher đổi điểm "${voucher.code}" này?`,
            header: 'Xác nhận sao chép',
            icon: 'pi pi-question-circle',
            acceptLabel: 'Sao chép',
            rejectLabel: 'Hủy',
            accept: async () => {
                try {
                    await bloc.cloneVoucher(voucher.id);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: 'Sao chép voucher đổi điểm thành công',
                        life: 3000
                    });
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: 'Không thể sao chép voucher đổi điểm',
                        life: 5000
                    });
                }
            }
        });
    };

    // Form submission handlers
    const handleFormSubmit = async (data: CreateVoucherRequest | UpdateVoucherRequest) => {
        try {
            if ('id' in data && data.id) {
                // Update existing voucher
                await bloc.updateVoucher(data as UpdateVoucherRequest);
                toast.current?.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: 'Cập nhật voucher đổi điểm thành công',
                    life: 3000
                });
            } else {
                // Create new voucher
                await bloc.createVoucher(data as CreateVoucherRequest);
                toast.current?.show({
                    severity: 'success',
                    summary: 'Thành công',
                    detail: 'Tạo voucher đổi điểm thành công',
                    life: 3000
                });
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: error instanceof Error ? error.message : 'Có lỗi xảy ra khi xử lý voucher',
                life: 5000
            });
        }
    };

    const getVoucherStatus = (voucher: VoucherWithCodes): 'active' | 'expired' | 'upcoming' => {
        const now = new Date();
        const startDate = new Date(voucher.start_date);
        const endDate = new Date(voucher.end_date);

        if (now < startDate) return 'upcoming';
        if (now > endDate) return 'expired';
        return 'active';
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('vi-VN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND'
        }).format(amount);
    };

    // Additional client-side filter to ensure only vouchers with point_cost > 0 are shown
    const filterRewardVouchers = (vouchers: VoucherWithCodes[]): VoucherWithCodes[] => {
        return vouchers.filter(voucher =>
            voucher.point_cost != null &&
            voucher.point_cost > 0
        );
    };

    // Column templates for DataTable
    const nameBodyTemplate = (rowData: VoucherWithCodes) => (
        <div className="voucher-name-cell">
            <div className="voucher-main-info">
                <span className="font-medium text-gray-900">{rowData.code}</span>
                <Tag
                    value="Đổi điểm"
                    style={{ backgroundColor: '#f39c12', color: 'white' }}
                    className="ml-2"
                />
            </div>
            {rowData.point_cost && (
                <div className="text-sm text-orange-600 font-medium">
                    Chi phí: {rowData.point_cost} điểm
                </div>
            )}
        </div>
    );

    const discountBodyTemplate = (rowData: VoucherWithCodes) => (
        <div className="discount-cell">
            {rowData.discount_value ? (
                <span className="font-medium">{formatCurrency(rowData.discount_value)}</span>
            ) : (
                <span className="font-medium">{rowData.discount_percentage}%</span>
            )}
            {rowData.minimum_amount && (
                <div className="text-sm text-gray-500">
                    Tối thiểu: {formatCurrency(rowData.minimum_amount)}
                </div>
            )}
        </div>
    );

    const statusBodyTemplate = (rowData: VoucherWithCodes) => {
        const status = getVoucherStatus(rowData);
        const statusConfig = {
            active: { label: 'Hoạt động', severity: 'success' as const },
            expired: { label: 'Đã hết hạn', severity: 'danger' as const },
            upcoming: { label: 'Sắp diễn ra', severity: 'warning' as const }
        };

        return <Tag value={statusConfig[status].label} severity={statusConfig[status].severity} />;
    };

    const usageBodyTemplate = (rowData: VoucherWithCodes) => (
        <div className="usage-cell">
            <div className="font-medium">
                {rowData.total_redemptions}/{rowData.total_redemption_limit || '∞'}
            </div>
            <div className="text-sm text-gray-500">đã sử dụng</div>
        </div>
    );

    const vehicleBodyTemplate = (rowData: VoucherWithCodes) => {
        if (!rowData.voucher_type_vehicle || rowData.voucher_type_vehicle === 'both') {
            return <Tag value="Tất cả" severity="info" />;
        }
        return (
            <Tag
                value={rowData.voucher_type_vehicle === 'car' ? 'Ô tô' : 'Xe máy'}
                severity="info"
            />
        );
    };

    const dateTemplate = (dateString: string) => (
        <span className="text-sm text-gray-600">
            {formatDate(dateString)}
        </span>
    );

    const actionBodyTemplate = (rowData: VoucherWithCodes) => (
        <div className="flex gap-2">
            {canEditVoucher && (
                <Button
                    variant="secondary"
                    size="small"
                    onClick={() => bloc.openEditForm(rowData)}
                >
                    <i className="pi pi-pencil"></i>
                </Button>
            )}
            <Button
                variant="secondary"
                size="small"
                onClick={() => handleCloneVoucher(rowData)}
            >
                <i className="pi pi-copy"></i>
            </Button>
            {canDeleteVoucher && (
                <Button
                    variant="danger"
                    size="small"
                    onClick={() => handleDeleteVoucher(rowData)}
                >
                    <i className="pi pi-trash"></i>
                </Button>
            )}
        </div>
    );

    // Filter options
    const statusOptions = [
        { label: 'Tất cả trạng thái', value: '' },
        { label: 'Hoạt động', value: 'active' },
        { label: 'Đã hết hạn', value: 'expired' },
        { label: 'Sắp diễn ra', value: 'upcoming' }
    ];

    const vehicleTypeOptions = [
        { label: 'Tất cả phương tiện', value: '' },
        { label: 'Ô tô', value: 'car' },
        { label: 'Xe máy', value: 'motorbike' },
        { label: 'Tất cả', value: 'both' }
    ];

    return (
        <AdminProtectedRoute
            requiredModule="voucher_management"
            requiredSubModule="vouchers_reward"
            requiredPermission="view"
        >
            <AdminLayout>
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Toast ref={toast} />
                    <ConfirmDialog />

                    {/* Page Header */}
                    <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb', background: 'white' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#1f2937' }}>
                                    Voucher Đổi Điểm
                                </h1>
                                <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                                    Quản lý voucher đổi điểm - chỉ hiển thị voucher có chi phí điểm lớn hơn 0
                                </p>
                            </div>
                            <Button
                                variant="secondary"
                                size="small"
                                onClick={() => setShowPageHelp(true)}
                            >
                                <i className="pi pi-question-circle" style={{ marginRight: '4px' }}></i>
                                Hướng dẫn
                            </Button>
                        </div>
                    </div>

                    {/* Table Container */}
                    <div style={{ flex: 1, minHeight: 0, background: 'white' }}>
                        <StreamBuilder
                            stream={bloc.getStream('voucherViewState')}
                            initialData={bloc.info}
                            builder={(snapshot) => {
                                const state: VoucherViewState = snapshot.data || bloc.info;

                                if (state.error) {
                                    return (
                                        <div style={{ padding: '24px' }}>
                                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '16px' }}>
                                                <div style={{ display: 'flex' }}>
                                                    <i className="pi pi-exclamation-triangle" style={{ color: '#f87171', marginRight: '8px' }}></i>
                                                    <div style={{ color: '#991b1b' }}>
                                                        <h3 style={{ fontSize: '0.875rem', fontWeight: '500' }}>Có lỗi xảy ra</h3>
                                                        <p style={{ fontSize: '0.875rem', marginTop: '4px' }}>{state.error}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                // Header template with filters
                                const headerTemplate = (
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '16px',
                                        alignItems: 'center',
                                        padding: '16px 24px',
                                        borderBottom: '1px solid #e5e7eb',
                                        background: '#f9fafb',
                                        width: '100%'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 250px', minWidth: '250px' }}>
                                            <i className="pi pi-search" style={{ color: '#6b7280', fontSize: '0.875rem' }}></i>
                                            <input
                                                type="text"
                                                placeholder="Tìm kiếm mã voucher đổi điểm..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                style={{
                                                    flex: 1,
                                                    fontSize: '0.875rem',
                                                    padding: '8px 12px',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '6px',
                                                    minWidth: '200px'
                                                }}
                                            />
                                        </div>

                                        <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                                            <Button
                                                variant="secondary"
                                                size="small"
                                                onClick={clearFilters}
                                            >
                                                <i className="pi pi-filter-slash"></i>
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="small"
                                                onClick={handleRefresh}
                                            >
                                                <i className="pi pi-refresh"></i>
                                            </Button>
                                            {canAddVoucher && (
                                                <Button
                                                    variant="primary"
                                                    size="small"
                                                    onClick={() => bloc.openCreateForm()}
                                                >
                                                    <i className="pi pi-plus" style={{ marginRight: '4px' }}></i>
                                                    Thêm voucher
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );

                                return (
                                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        {/* Table */}
                                        <div style={{ flex: 1, overflow: 'hidden' }}>
                                            <DataTable
                                                scrollHeight='calc(100vh - 200px)'
                                                value={filterRewardVouchers(state.vouchers)}
                                                loading={state.loading}
                                                loadingIcon="pi pi-spinner pi-spin"
                                                className="vouchers-management-table"
                                                emptyMessage={searchTerm ?
                                                    'Không tìm thấy voucher đổi điểm nào có chi phí điểm phù hợp' :
                                                    'Chưa có voucher đổi điểm nào có chi phí điểm'
                                                }
                                                responsiveLayout="scroll"
                                                scrollable

                                                size="small"
                                                stripedRows
                                                header={headerTemplate}
                                                showGridlines
                                                paginator={filterRewardVouchers(state.vouchers).length > 0}
                                                rows={state.pagination.limit}
                                                totalRecords={filterRewardVouchers(state.vouchers).length}
                                                first={0}
                                                onPage={() => { }}
                                                rowsPerPageOptions={[10, 20, 50]}
                                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                                                currentPageReportTemplate="Hiển thị {first} đến {last} trong tổng số {totalRecords} voucher có chi phí điểm"
                                            >
                                                <Column
                                                    field="code"
                                                    header="Mã voucher"
                                                    body={nameBodyTemplate}
                                                    style={{ minWidth: '200px' }}
                                                    frozen
                                                />
                                                <Column
                                                    field="discount"
                                                    header="Giảm giá"
                                                    body={discountBodyTemplate}
                                                    style={{ minWidth: '150px' }}
                                                />
                                                <Column
                                                    field="status"
                                                    header="Trạng thái"
                                                    body={statusBodyTemplate}
                                                    style={{ minWidth: '120px' }}
                                                    filter
                                                    showFilterMenuOptions={false}
                                                    showClearButton={false}
                                                    filterElement={
                                                        <Dropdown
                                                            value={selectedStatus}
                                                            options={statusOptions}
                                                            onChange={(e) => handleStatusFilter(e.value)}
                                                            placeholder="Tất cả trạng thái"
                                                            style={{ width: '100%', fontSize: '0.75rem' }}
                                                            className="p-column-filter"
                                                        />
                                                    }
                                                />

                                                <Column
                                                    field="usage"
                                                    header="Sử dụng"
                                                    body={usageBodyTemplate}
                                                    style={{ minWidth: '100px' }}
                                                />
                                                <Column
                                                    field="vehicle_type"
                                                    header="Phương tiện"
                                                    body={vehicleBodyTemplate}
                                                    style={{ minWidth: '120px' }}
                                                    filter
                                                    showFilterMenuOptions={false}
                                                    showClearButton={false}
                                                    filterElement={
                                                        <Dropdown
                                                            value={selectedVehicleType}
                                                            options={vehicleTypeOptions}
                                                            onChange={(e) => handleVehicleTypeFilter(e.value)}
                                                            placeholder="Tất cả phương tiện"
                                                            style={{ width: '100%', fontSize: '0.75rem' }}
                                                            className="p-column-filter"
                                                        />
                                                    }
                                                />
                                                <Column
                                                    field="start_date"
                                                    header="Bắt đầu"
                                                    body={(rowData) => dateTemplate(rowData.start_date)}
                                                    sortable
                                                    style={{ minWidth: '150px' }}
                                                />
                                                <Column
                                                    field="end_date"
                                                    header="Kết thúc"
                                                    body={(rowData) => dateTemplate(rowData.end_date)}
                                                    sortable
                                                    style={{ minWidth: '150px' }}
                                                />
                                                <Column
                                                    header="Thao tác"
                                                    body={actionBodyTemplate}
                                                    style={{ minWidth: '120px' }}
                                                    frozen
                                                    alignFrozen="right"
                                                />
                                            </DataTable>
                                        </div>

                                        {/* Show message when no data and not loading */}
                                        {!state.loading && filterRewardVouchers(state.vouchers).length === 0 && (
                                            <div style={{ flexShrink: 0, padding: '32px', textAlign: 'center' }}>
                                                <div style={{ color: '#6b7280' }}>
                                                    <i className="pi pi-info-circle" style={{ fontSize: '2rem', marginBottom: '12px', display: 'block' }}></i>
                                                    <p style={{ fontSize: '1.125rem', fontWeight: '500' }}>Chưa có voucher đổi điểm với chi phí điểm</p>
                                                    <p style={{ fontSize: '0.875rem', marginTop: '4px' }}>Hệ thống chưa có voucher đổi điểm nào có chi phí điểm lớn hơn 0.</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            }}
                        />
                    </div>
                </div>

                {/* Voucher Form - Inside StreamBuilder to react to state changes */}
                <StreamBuilder
                    stream={bloc.getStream('voucherViewState')}
                    initialData={bloc.info}
                    builder={(snapshot) => {
                        const state: VoucherViewState = snapshot.data || bloc.info;
                        return (
                            <VoucherRewardForm
                                visible={state.isFormOpen}
                                voucher={state.selectedVoucher}
                                onHide={() => bloc.closeForm()}
                                onSubmit={handleFormSubmit}
                                loading={state.loading}
                                isEdit={state.formType === 'edit'}
                            />
                        );
                    }}
                />

                {/* Page Help Dialog */}
                <Dialog
                    header="Hướng dẫn Quản lý Voucher Đổi Điểm"
                    visible={showPageHelp}
                    onHide={() => setShowPageHelp(false)}
                    modal
                    className="page-help-dialog"
                    style={{ width: '800px' }}
                >
                    <div className="page-help-content">
                        <div className="help-section">
                            <h3><i className="pi pi-info-circle"></i> Voucher Đổi Điểm là gì?</h3>
                            <p>Voucher đổi điểm là loại voucher đặc biệt trong chương trình tích điểm thưởng. Người dùng cần tích lũy đủ điểm để có thể đổi lấy voucher này.</p>
                            <p><strong>Lưu ý:</strong> Trang này chỉ hiển thị các voucher có chi phí điểm lớn hơn 0, đảm bảo đây là voucher thực sự cần đổi điểm.</p>
                        </div>

                        <div className="help-section">
                            <h3><i className="pi pi-cog"></i> Các trường bắt buộc</h3>
                            <ul>
                                <li><strong>voucher_type = 'reward':</strong> Đánh dấu voucher là loại đổi điểm</li>
                                <li><strong>code:</strong> Mã code cố định cho voucher (lưu trực tiếp trong bảng voucher)</li>
                                <li><strong>point_cost:</strong> Chi phí điểm (bắt buộc &gt; 0 cho voucher đổi điểm)</li>
                                <li><strong>discount_value hoặc discount_percentage:</strong> Xác định giá trị giảm giá (chỉ một trong hai)</li>
                                <li><strong>start_date và end_date:</strong> Thời gian hiệu lực của voucher</li>
                                <li><strong>max_usage:</strong> Số lần tối đa người dùng có thể áp dụng (mặc định 1)</li>
                            </ul>
                        </div>

                        <div className="help-section">
                            <h3><i className="pi pi-list"></i> Các trường tùy chọn</h3>
                            <ul>
                                <li><strong>minimum_amount:</strong> Giá trị đơn hàng tối thiểu để áp dụng</li>
                                <li><strong>voucher_type_vehicle:</strong> Loại xe áp dụng (car, motorbike, both)</li>
                                <li><strong>total_redemption_limit:</strong> Giới hạn tổng số lần voucher có thể được đổi bởi tất cả người dùng</li>
                                <li><strong>user_redemption_limit:</strong> Số lần đổi tối đa của mỗi người dùng - giới hạn số lần một người có thể đổi voucher này từ điểm tích lũy</li>
                            </ul>
                        </div>

                        <div className="help-section">
                            <h3><i className="pi pi-sync"></i> Cách thức hoạt động</h3>
                            <ul>
                                <li>Admin tạo voucher đổi điểm với chi phí điểm cụ thể</li>
                                <li>Voucher hiển thị trong danh sách đổi điểm của ứng dụng người dùng</li>
                                <li>Người dùng có đủ điểm có thể đổi voucher</li>
                                <li>Hệ thống trừ điểm và cấp mã voucher cho người dùng</li>
                                <li>Người dùng sử dụng mã voucher khi đặt chuyến xe</li>
                            </ul>
                        </div>

                        <div className="help-section">
                            <h3><i className="pi pi-lightbulb"></i> Lưu ý quan trọng</h3>
                            <ul>
                                <li>Chi phí điểm phải lớn hơn 0 (bắt buộc cho voucher đổi điểm)</li>
                                <li>Mã code phải là duy nhất trong hệ thống</li>
                                <li>Nên cân nhắc tỷ lệ điểm/giá trị để tạo động lực cho người dùng</li>
                                <li>Voucher đổi điểm sẽ hiển thị trong mục "Đổi điểm" của ứng dụng</li>
                                <li>Chỉ có thể chọn một trong hai: giảm theo số tiền hoặc phần trăm</li>
                            </ul>
                        </div>
                    </div>
                </Dialog>
            </AdminLayout>
        </AdminProtectedRoute>
    );
};

export default VoucherRewardPage;
